import { useState, useEffect, useRef } from 'react';
import { ArrowDown, Sparkles, Globe, Mail, MapPin } from 'lucide-react';
import { gsap } from 'gsap';
import { Button } from '@/components/ui/button';
import { prefersReducedMotion, heroTextAnimation } from '@/hooks/useGsap';
import { trackEvent } from '@/lib/analytics';

const LOGO_SVGS: Record<string, React.ReactNode> = {
  n8n: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="18" r="2" />
      <circle cx="18" cy="6" r="2" />
      <line x1="8" y1="8" x2="10" y2="10" />
      <line x1="14" y1="14" x2="16" y2="16" />
      <line x1="16" y1="8" x2="14" y2="10" />
    </svg>
  ),
  Anthropic: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.02 21H21l-7.38-18h-3.24L3 21h3.98l1.62-4.14h6.8l1.62 4.14zm-7.3-6.84L12 8.76l2.28 5.4h-4.56z"/>
    </svg>
  ),
  ChatGPT: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21.36 11.11a3.7 3.7 0 0 0-.1-1.39 3.52 3.52 0 0 0-.81-1.37l-.07-.07a3.73 3.73 0 0 0-1.33-.87 3.7 3.7 0 0 0-1.49-.22 3.65 3.65 0 0 0-1.42.38 3.56 3.56 0 0 0-1.16.89l.07.04a2.22 2.22 0 0 1 .93-.16 2.26 2.26 0 0 1 1.06.41l3.18 1.84c.38.22.69.53.9.9a2.3 2.3 0 0 1 .24 1.13c0 .41-.1.81-.31 1.16a2.24 2.24 0 0 1-.9.84l-3.18 1.84c-.38.22-.82.33-1.26.33a2.3 2.3 0 0 1-1.2-.33l-1.09-.63v1.26a3.56 3.56 0 0 0 .58 1.95c.34.52.8 1 1.34 1.34s1.14.54 1.77.58a3.76 3.76 0 0 0 1.93-.24 3.72 3.72 0 0 0 1.52-1.08 3.6 3.6 0 0 0 .86-1.42 3.53 3.53 0 0 0-.08-2.61zm-11-7.18a3.52 3.52 0 0 0-1.37-.81A3.73 3.73 0 0 0 7.6 3a3.65 3.65 0 0 0-1.42.38 3.56 3.56 0 0 0-1.16.89l.04.07a2.22 2.22 0 0 1 .93-.16 2.26 2.26 0 0 1 1.06.41l3.18 1.84c.38.22.69.53.9.9s.31.75.31 1.13a2.3 2.3 0 0 1-.31 1.16 2.24 2.24 0 0 1-.9.84L7.05 13.4a2.3 2.3 0 0 1-2.46 0l-1.09-.63V14a3.56 3.56 0 0 0 .58 1.95c.34.52.8 1 1.34 1.34s1.14.54 1.77.58a3.76 3.76 0 0 0 1.93-.24 3.72 3.72 0 0 0 1.52-1.08A3.6 3.6 0 0 0 12 15.13a3.53 3.53 0 0 0-.08-2.61v-.09l1.09-.63a2.22 2.22 0 0 1 1.08-.2 2.25 2.25 0 0 1 1-1v.09l-3.18 1.84c-.38.22-.69.53-.9.9a2.3 2.3 0 0 1-.24 1.13c0 .41.1.81.31 1.16a2.24 2.24 0 0 1 .9.84l3.18-1.84c.38-.22.82-.33 1.26-.33a2.3 2.3 0 0 1 1.2.33l3.18 1.84a3.57 3.57 0 0 0 1.95-.58c.52-.34 1-.8 1.34-1.34s.54-1.14.58-1.77a3.76 3.76 0 0 0-.24-1.93 3.72 3.72 0 0 0-1.08-1.52A3.6 3.6 0 0 0 17 11.23a3.53 3.53 0 0 0-2.61.08l-1.09.63V10.68a2.22 2.22 0 0 1-.2-1.08 2.25 2.25 0 0 1 1-1l3.18-1.84c.38-.22.69-.53.9-.9a2.3 2.3 0 0 1 .24-1.13c0-.41-.1-.81-.31-1.16a2.24 2.24 0 0 1-.9-.84L15.31 4a2.3 2.3 0 0 1-2.46 0L11.76 3.4a3.57 3.57 0 0 0-1.95.58c-.52.34-1 .8-1.34 1.34s-.54 1.14-.58 1.77a3.76 3.76 0 0 0 .24 1.93c.27.57.64 1.08 1.08 1.52A3.6 3.6 0 0 0 11.77 12a3.53 3.53 0 0 0 2.61-.08l1.09-.63V10.68a2.22 2.22 0 0 1 .2-1.08 2.25 2.25 0 0 1-1-1l-3.18-1.84z"/>
    </svg>
  ),
  'z.ai': (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h18L3 21h18" />
      <circle cx="21" cy="21" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  LangChain: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  ),
  Graphify: (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="3" fill="currentColor" />
      <circle cx="5" cy="18" r="3" fill="currentColor" />
      <circle cx="19" cy="18" r="3" fill="currentColor" />
      <line x1="12" y1="8" x2="6.5" y2="15.5" />
      <line x1="12" y1="8" x2="17.5" y2="15.5" />
      <line x1="8" y1="18" x2="16" y2="18" />
    </svg>
  )
};

const BG_LOGOS = [
  { name: 'n8n', style: { left: '4%', top: '10%', animation: 'logo-float-1 16s ease-in-out infinite' } },
  { name: 'Anthropic', style: { left: '8%', top: '22%', animation: 'logo-float-2 18s ease-in-out infinite' } },
  { name: 'ChatGPT', style: { left: '3%', top: '38%', animation: 'logo-float-3 14s ease-in-out infinite' } },
  { name: 'z.ai', style: { left: '9%', top: '50%', animation: 'logo-float-1 15s ease-in-out infinite' } },
  { name: 'LangChain', style: { left: '2%', top: '65%', animation: 'logo-float-2 20s ease-in-out infinite' } },
  { name: 'Graphify', style: { left: '7%', top: '78%', animation: 'logo-float-3 17s ease-in-out infinite' } },
  { name: 'n8n', style: { left: '14%', top: '32%', animation: 'logo-float-1 19s ease-in-out infinite' } },
  { name: 'Anthropic', style: { left: '16%', top: '60%', animation: 'logo-float-2 15s ease-in-out infinite' } },
  { name: 'ChatGPT', style: { left: '20%', top: '12%', animation: 'logo-float-3 16s ease-in-out infinite' } },
  { name: 'z.ai', style: { left: '22%', top: '46%', animation: 'logo-float-1 18s ease-in-out infinite' } },
  { name: 'LangChain', style: { left: '18%', top: '88%', animation: 'logo-float-2 14s ease-in-out infinite' } },
  { name: 'Graphify', style: { left: '26%', top: '25%', animation: 'logo-float-3 16s ease-in-out infinite' } },
  { name: 'ChatGPT', style: { left: '32%', top: '8%', animation: 'logo-float-1 15s ease-in-out infinite' } },
  { name: 'z.ai', style: { left: '36%', top: '28%', animation: 'logo-float-2 16s ease-in-out infinite' } },
  { name: 'LangChain', style: { left: '30%', top: '54%', animation: 'logo-float-3 17s ease-in-out infinite' } },
  { name: 'Graphify', style: { left: '34%', top: '76%', animation: 'logo-float-1 20s ease-in-out infinite' } },
  { name: 'n8n', style: { left: '42%', top: '15%', animation: 'logo-float-2 18s ease-in-out infinite' } },
  { name: 'Anthropic', style: { left: '45%', top: '38%', animation: 'logo-float-3 19s ease-in-out infinite' } },
  { name: 'ChatGPT', style: { left: '40%', top: '62%', animation: 'logo-float-1 14s ease-in-out infinite' } },
  { name: 'z.ai', style: { left: '46%', top: '84%', animation: 'logo-float-2 16s ease-in-out infinite' } },
  { name: 'LangChain', style: { left: '52%', top: '22%', animation: 'logo-float-3 15s ease-in-out infinite' } },
  { name: 'Graphify', style: { left: '56%', top: '50%', animation: 'logo-float-1 18s ease-in-out infinite' } },
  { name: 'n8n', style: { left: '50%', top: '72%', animation: 'logo-float-2 22s ease-in-out infinite' } },
  { name: 'Anthropic', style: { left: '58%', top: '10%', animation: 'logo-float-3 14s ease-in-out infinite' } },
  { name: 'ChatGPT', style: { left: '64%', top: '30%', animation: 'logo-float-1 17s ease-in-out infinite' } },
  { name: 'z.ai', style: { left: '68%', top: '56%', animation: 'logo-float-2 19s ease-in-out infinite' } },
  { name: 'LangChain', style: { left: '62%', top: '82%', animation: 'logo-float-3 15s ease-in-out infinite' } },
  { name: 'Graphify', style: { left: '72%', top: '18%', animation: 'logo-float-1 16s ease-in-out infinite' } },
  { name: 'n8n', style: { left: '76%', top: '44%', animation: 'logo-float-2 18s ease-in-out infinite' } },
  { name: 'Anthropic', style: { left: '70%', top: '70%', animation: 'logo-float-3 14s ease-in-out infinite' } },
  { name: 'ChatGPT', style: { left: '82%', top: '12%', animation: 'logo-float-1 15s ease-in-out infinite' } },
  { name: 'z.ai', style: { left: '85%', top: '34%', animation: 'logo-float-2 20s ease-in-out infinite' } },
  { name: 'LangChain', style: { left: '80%', top: '58%', animation: 'logo-float-3 16s ease-in-out infinite' } },
  { name: 'Graphify', style: { left: '86%', top: '86%', animation: 'logo-float-1 18s ease-in-out infinite' } },
  { name: 'n8n', style: { left: '92%', top: '24%', animation: 'logo-float-2 15s ease-in-out infinite' } },
  { name: 'Anthropic', style: { left: '94%', top: '64%', animation: 'logo-float-3 17s ease-in-out infinite' } }
];

const Hero = () => {
  const [activeNode, setActiveNode] = useState<number | null>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (containerRef.current) {
      heroTextAnimation(containerRef.current);
    }
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let step = 0;
    const interval = setInterval(() => {
      step = (step + 1) % 4;
      setActiveNode(step);
    }, 2800);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    // 1. Mouse Parallax
    const hero = document.getElementById('home');
    if (hero) {
      const handleMouseMove = (e: MouseEvent) => {
        const { clientX, clientY } = e;
        const xPos = (clientX - window.innerWidth / 2) / (window.innerWidth / 2);
        const yPos = (clientY - window.innerHeight / 2) / (window.innerHeight / 2);

        gsap.to('.hero-canvas-container', {
          x: xPos * 25,
          y: yPos * 25,
          rotateY: xPos * 8,
          rotateX: -yPos * 8,
          duration: 0.8,
          ease: 'power2.out',
          transformPerspective: 1000
        });
      };

      hero.addEventListener('mousemove', handleMouseMove);
      return () => {
        hero.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    // 2. Magnetic CTAs
    const buttons = document.querySelectorAll('.hero-cta');
    const cleanups: (() => void)[] = [];

    buttons.forEach(button => {
      const handleMouseMove = (e: MouseEvent) => {
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        gsap.to(button, {
          x: x * 0.35,
          y: y * 0.35,
          duration: 0.3,
          ease: 'power2.out'
        });
      };

      const handleMouseLeave = () => {
        gsap.to(button, {
          x: 0,
          y: 0,
          duration: 0.6,
          ease: 'elastic.out(1, 0.3)'
        });
      };

      button.addEventListener('mousemove', handleMouseMove as EventListener);
      button.addEventListener('mouseleave', handleMouseLeave);

      cleanups.push(() => {
        button.removeEventListener('mousemove', handleMouseMove as EventListener);
        button.removeEventListener('mouseleave', handleMouseLeave);
      });
    });

    return () => {
      cleanups.forEach(cleanup => cleanup());
    };
  }, []);

  const titlePieceStyle = { opacity: prefersReducedMotion() ? 1 : 0 };

  return (
    <section
      id="home"
      className="hero-section relative isolate min-h-[84svh] overflow-hidden bg-background text-foreground"
      style={{ paddingTop: 'var(--navbar-height)' }}
    >
      <div className="hero-css-scene" data-testid="hero-css-scene" aria-hidden="true">
        <div className="hero-grid-field" />

        {/* Floating Brand Logos Background Cloud */}
        {!prefersReducedMotion() && BG_LOGOS.map((logo, index) => (
          <div
            key={index}
            className="hero-bg-logo select-none pointer-events-auto"
            style={logo.style}
            title={logo.name}
          >
            {LOGO_SVGS[logo.name]}
          </div>
        ))}
        
        {/* Ambient Mesh Orbs */}
        <div className="hero-mesh-orb hero-orb-1" />
        <div className="hero-mesh-orb hero-orb-2" />
        <div className="hero-mesh-orb hero-orb-3" />

        {/* Full-width Background Logo Ticker */}
        <div className="hero-marquee-container">
          <div className="hero-marquee-wrapper">
            <div className="hero-marquee-content">
              <span>MAKE IT EASY+</span>
              <span>•</span>
              <span>CLAUDE FABLE 5</span>
              <span>•</span>
              <span>AI WORKFLOWS</span>
              <span>•</span>
              <span>AGENTIC FLOWS</span>
              <span>•</span>
            </div>
            <div className="hero-marquee-content" aria-hidden="true">
              <span>MAKE IT EASY+</span>
              <span>•</span>
              <span>CLAUDE FABLE 5</span>
              <span>•</span>
              <span>AI WORKFLOWS</span>
              <span>•</span>
              <span>AGENTIC FLOWS</span>
              <span>•</span>
            </div>
          </div>
        </div>

        {/* Full-width Agentic Flow Background Network */}
        {!prefersReducedMotion() && (
          <div className="absolute inset-0 z-[2] pointer-events-none select-none opacity-30 dark:opacity-[0.22]">
            {/* SVG Connector Lines and Flowing Packets */}
            <svg className="w-full h-full stroke-foreground/15 dark:stroke-foreground/10" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Path 1: Top-Left to Center */}
              <path
                id="bg-path-1"
                d="M 15 15 L 45 45"
                fill="none"
                strokeWidth="0.15"
                strokeDasharray="1 1"
              />
              {/* Path 2: Center to Bottom-Right */}
              <path
                id="bg-path-2"
                d="M 45 45 L 85 80"
                fill="none"
                strokeWidth="0.15"
                strokeDasharray="1 1"
              />
              {/* Path 3: Top-Right to Center */}
              <path
                id="bg-path-3"
                d="M 80 15 L 45 45"
                fill="none"
                strokeWidth="0.15"
                strokeDasharray="1 1"
              />
              {/* Path 4: Center to Bottom-Left */}
              <path
                id="bg-path-4"
                d="M 45 45 L 15 80"
                fill="none"
                strokeWidth="0.15"
                strokeDasharray="1 1"
              />

              {/* Animated flowing packets */}
              <circle r="0.4" fill="currentColor" className="text-foreground/45">
                <animateMotion dur="6s" repeatCount="indefinite" path="M 15 15 L 45 45" />
              </circle>
              <circle r="0.4" fill="currentColor" className="text-foreground/45">
                <animateMotion dur="8s" repeatCount="indefinite" path="M 45 45 L 85 80" />
              </circle>
              <circle r="0.4" fill="currentColor" className="text-foreground/45">
                <animateMotion dur="7s" repeatCount="indefinite" path="M 80 15 L 45 45" />
              </circle>
              <circle r="0.4" fill="currentColor" className="text-foreground/45">
                <animateMotion dur="9s" repeatCount="indefinite" path="M 45 45 L 15 80" />
              </circle>
            </svg>

            {/* Nodes floating in the background matching the path endpoints */}
            {/* Node 1: Top-Left (n8n) */}
            <div className="absolute left-[15%] top-[15%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-none border border-border/30 bg-card/30 flex items-center justify-center text-foreground/50 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <circle cx="6" cy="6" r="2" />
                  <circle cx="18" cy="18" r="2" />
                  <circle cx="18" cy="6" r="2" />
                  <line x1="8" y1="8" x2="10" y2="10" />
                  <line x1="14" y1="14" x2="16" y2="16" />
                  <line x1="16" y1="8" x2="14" y2="10" />
                </svg>
              </div>
              <span className="text-[6.5px] font-mono font-bold tracking-wider text-muted-foreground uppercase">[n8n]</span>
            </div>

            {/* Node 2: Center (Fable 5 Engine) */}
            <div className="absolute left-[45%] top-[45%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
              <div className="w-10 h-10 rounded-none border border-primary/20 bg-primary/5 dark:bg-primary/10 flex items-center justify-center text-foreground/75 font-black text-[9px] animate-pulse">
                F5
              </div>
              <span className="text-[6.5px] font-mono font-bold tracking-wider text-primary/70 uppercase">[Fable 5]</span>
            </div>

            {/* Node 3: Top-Right (Router) */}
            <div className="absolute left-[80%] top-[15%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-none border border-border/30 bg-card/30 flex items-center justify-center text-foreground/50 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <span className="text-[6.5px] font-mono font-bold tracking-wider text-muted-foreground uppercase">[Router]</span>
            </div>

            {/* Node 4: Bottom-Right (Slack Msg Dispatcher) */}
            <div className="absolute left-[85%] top-[80%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-none border border-border/30 bg-card/30 flex items-center justify-center text-foreground/50 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523 2.528 2.528 0 0 1-2.522-2.523 2.528 2.528 0 0 1 2.522-2.52h2.52v2.52zm1.261 0a2.528 2.528 0 0 1 2.52-2.52h5.043a2.528 2.528 0 0 1 2.522 2.52v5.042a2.528 2.528 0 0 1-2.522 2.52H8.823a2.528 2.528 0 0 1-2.52-2.52v-5.042z"/>
                </svg>
              </div>
              <span className="text-[6.5px] font-mono font-bold tracking-wider text-muted-foreground uppercase">[Slack]</span>
            </div>

            {/* Node 5: Bottom-Left (Make.com Automation) */}
            <div className="absolute left-[15%] top-[80%] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-none border border-border/30 bg-card/30 flex items-center justify-center text-foreground/50 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                  <path d="M2 12a14.5 14.5 0 0 0 20 0 14.5 14.5 0 0 0-20 0" />
                </svg>
              </div>
              <span className="text-[6.5px] font-mono font-bold tracking-wider text-muted-foreground uppercase">[Make]</span>
            </div>
          </div>
        )}
      </div>
      <div className="hero-overlay" />
      <div className="absolute inset-x-0 bottom-0 z-[2] h-40 bg-gradient-to-t from-background to-transparent" />

      <div className="container-site relative z-10 flex min-h-[calc(84svh-var(--navbar-height))] items-start py-10 sm:items-center sm:py-16 lg:py-20 pointer-events-none">
        <div ref={containerRef} className="w-full min-w-0 max-w-4xl text-left sm:text-center lg:text-left pointer-events-auto">

          <h1 className="max-w-4xl font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light uppercase leading-[0.9] tracking-[0.03em] text-foreground">
            <span className="hero-title-piece hero-word hero-animate block lg:inline-block" style={titlePieceStyle}>
              Make AI Automation{' '}
            </span>{' '}
            <span className="hero-title-piece hero-word hero-animate block lg:inline-block" style={titlePieceStyle}>
              Feel{' '}
            </span>
            <span
              className="hero-title-piece hero-word hero-animate mt-1 block font-normal"
              style={titlePieceStyle}
            >
              Easy.
            </span>
          </h1>

          <p
            className="hero-copy hero-subtitle hero-animate mt-7 break-words text-xs uppercase tracking-wider leading-6 text-muted-foreground sm:mx-auto lg:mx-0"
            style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
          >
            Practical AI automation, trustworthy AI/security notes, and builder experiments.
            Bikin teknologi kompleks jadi simpel, bareng-bareng.
          </p>

          <div className="hero-metrics mt-7 flex max-w-full flex-wrap gap-2 text-[10px] font-bold uppercase text-foreground sm:justify-center lg:justify-start">
            <span>AI automation</span>{' '}
            <span>AI & security notes</span>{' '}
            <span>Builder experiments</span>
          </div>

          <div className="mt-9 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center lg:justify-start">
            <Button
              size="lg"
              className="h-11 w-full max-w-xs rounded-none bg-foreground text-background border border-foreground px-6 font-bold uppercase transition-all duration-200 sm:w-auto sm:max-w-none hero-cta hero-animate hover:bg-background hover:text-foreground"
              style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
              onClick={() => {
                trackEvent('cta_clicked', { cta_name: 'explore_services', section: 'hero' });
                handleScroll('#services');
              }}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Explore Services
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-11 w-full max-w-xs rounded-none border-border bg-card px-6 font-bold uppercase text-foreground hover:bg-muted transition-all duration-200 sm:w-auto sm:max-w-none hero-cta hero-animate"
              style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
              onClick={() => {
                trackEvent('cta_clicked', { cta_name: 'learn_together', section: 'hero' });
                handleScroll('#contact');
              }}
            >
              Learn Together
            </Button>
          </div>

          {/* Info bar matching magnet.id bottom bar */}
          <div
            className="hero-cta hero-animate mt-12 pt-8 border-t border-border flex flex-wrap gap-x-8 gap-y-4 text-[10px] uppercase font-bold text-muted-foreground sm:justify-center lg:justify-start"
            style={{ opacity: prefersReducedMotion() ? 1 : 0 }}
          >
            <a href="https://letsmakeiteasy.work" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-foreground transition-colors">
              <Globe className="h-4 w-4" />
              <span>www.letsmakeiteasy.work</span>
            </a>
            <a href="mailto:info@letsmakeiteasy.work" className="flex items-center gap-2 hover:text-foreground transition-colors">
              <Mail className="h-4 w-4" />
              <span>info@letsmakeiteasy.work</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>Indonesia</span>
            </span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-7 left-1/2 z-10 hidden -translate-x-1/2 sm:block">
        <button
          type="button"
          onClick={() => handleScroll('#about')}
          className="rounded-none border border-border bg-card p-3 text-foreground transition-colors hover:bg-muted"
          aria-label="Scroll to about section"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      </div>
    </section>
  );
};

export default Hero;
